import { DeadLetterJob, ProviderOverview, ReplayResult, SlaItem, SlaOverview, type SlaState } from '@rp/contracts'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { useReadySession } from '../../session/useSession'

export const DEAD_LETTER_PAGE_LIMIT = 20
export const SLA_PAGE_LIMIT = 20

export const operationsKeys = {
  all: ['operations'] as const,
  sla: (tenantId: string) => ['operations', 'sla', tenantId] as const,
  slaItems: (tenantId: string, state: SlaState, page: number) =>
    ['operations', 'sla', tenantId, state, page] as const,
  providers: (tenantId: string) => ['operations', 'providers', tenantId] as const,
  deadLetters: (tenantId: string) => ['operations', 'dead-letters', tenantId] as const,
}

export const useSlaOverview = () => {
  const { api, membership } = useReadySession()
  return useQuery({
    queryKey: operationsKeys.sla(membership.tenantId),
    queryFn: async () => (await api.request(SlaOverview, { method: 'GET', path: '/v1/operations/sla' })).data,
  })
}

export const useSlaItems = (state: SlaState, page: number) => {
  const { api, membership } = useReadySession()
  return useQuery({
    queryKey: operationsKeys.slaItems(membership.tenantId, state, page),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const result = await api.request(z.array(SlaItem), {
        method: 'GET',
        path: '/v1/operations/sla/items',
        query: { state, page, limit: SLA_PAGE_LIMIT },
      })
      return { items: result.data, total: result.meta?.total ?? result.data.length }
    },
  })
}

export const useProviderOverview = () => {
  const { api, membership } = useReadySession()
  return useQuery({
    queryKey: operationsKeys.providers(membership.tenantId),
    queryFn: async () =>
      (await api.request(ProviderOverview, { method: 'GET', path: '/v1/operations/providers' })).data,
  })
}

export const useDeadLetters = () => {
  const { api, membership } = useReadySession()
  return useQuery({
    queryKey: operationsKeys.deadLetters(membership.tenantId),
    queryFn: async () => {
      const result = await api.request(z.array(DeadLetterJob), {
        method: 'GET',
        path: '/v1/operations/dead-letters',
        query: { limit: DEAD_LETTER_PAGE_LIMIT },
      })
      return { jobs: result.data, total: result.meta?.total ?? result.data.length }
    },
  })
}

export const useReplayDeadLetter = () => {
  const { api, membership } = useReadySession()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (jobId: string) =>
      (
        await api.request(ReplayResult, {
          method: 'POST',
          path: `/v1/operations/dead-letters/${jobId}/replay`,
        })
      ).data,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: operationsKeys.deadLetters(membership.tenantId) }),
  })
}
